import { useCallback } from 'react';
import type { StripeError } from '@stripe/stripe-js';
import { errorMessage } from '@firsttable/functions';
import {
  type BookingErrorRecoverability,
  type BookingErrorSource,
} from '@discover-lib/bookingErrors';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { flattenObjectByStringifying } from '@/lib/utils';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';
import getNormalisedError from './getNormalisedError';

type BookingErrorClassification = {
  source: BookingErrorSource;
  recoverability: BookingErrorRecoverability;
};

export default function useLogError() {
  const { track } = useAnalytics();

  const logError = useCallback(
    (
      error: Error | StripeError | string,
      classification: BookingErrorClassification,
      customAttributes?: Record<
        string,
        string | number | boolean | object | unknown[] | null | undefined
      >,
      defaultMessage?: string
    ) => {
      const message = errorMessage(error, defaultMessage);

      const bookingErrorAttributes = {
        ...customAttributes,
        // Stable discriminator so the dashboard never depends on `errorClass`.
        booking_error: true,
        booking_error_source: classification.source,
        booking_error_recoverability: classification.recoverability,
      };

      reportErrorToNewRelic(
        getNormalisedError(error, message),
        flattenObjectByStringifying(bookingErrorAttributes)
      );

      track('booking_error', { ...bookingErrorAttributes, error_message: message });
    },
    [track]
  );

  return logError;
}
